import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/Logo';
import { Loader2 } from '@/components/icons';
import { useHealth } from '@/hooks/useHealth';

type Level = 'operational' | 'degraded' | 'down' | 'planned' | 'unknown';

const DOT: Record<Level, string> = {
  operational: 'bg-success',
  degraded: 'bg-warning',
  down: 'bg-danger',
  planned: 'bg-ink-faint',
  unknown: 'bg-ink-faint animate-pulse-soft',
};

const LABEL: Record<Level, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  down: 'Outage',
  planned: 'Not yet enabled',
  unknown: 'Checking…',
};

function StatusPill({ level }: { level: Level }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm font-medium text-ink">
      <span className={cn('h-2.5 w-2.5 rounded-full', DOT[level])} />
      {LABEL[level]}
    </span>
  );
}

function Component({ name, level, note }: { name: string; level: Level; note?: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4 last:border-0">
      <div>
        <p className="text-sm font-medium text-ink">{name}</p>
        {note && <p className="text-xs text-ink-faint">{note}</p>}
      </div>
      <StatusPill level={level} />
    </div>
  );
}

export function StatusPage() {
  const { data, isError, isLoading, dataUpdatedAt, refetch, isFetching } = useHealth();

  const apiLevel: Level = isLoading ? 'unknown' : isError ? 'down' : 'operational';
  const dbLevel: Level = isLoading
    ? 'unknown'
    : isError
      ? 'unknown'
      : data?.db === 'ok'
        ? 'operational'
        : 'degraded';

  const overall: Level =
    apiLevel === 'down' ? 'down' : dbLevel === 'degraded' ? 'degraded' : apiLevel === 'unknown' ? 'unknown' : 'operational';

  const overallText =
    overall === 'operational'
      ? 'All systems operational'
      : overall === 'degraded'
        ? 'Some systems degraded'
        : overall === 'down'
          ? 'We’re experiencing an outage'
          : 'Checking system status…';

  const overallBg =
    overall === 'operational'
      ? 'from-success/20'
      : overall === 'degraded'
        ? 'from-warning/20'
        : overall === 'down'
          ? 'from-danger/20'
          : 'from-brand/20';

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <div className="mb-8 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm text-ink-muted hover:text-ink">
            ← Back to BingeBox
          </Link>
        </div>

        <div className={cn('card relative overflow-hidden bg-gradient-to-br to-transparent p-6', overallBg)}>
          <div className="flex items-center gap-3">
            <span className={cn('h-3.5 w-3.5 rounded-full', DOT[overall])} />
            <h1 className="font-display text-2xl font-bold text-ink">{overallText}</h1>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {data?.environment ? `Environment: ${data.environment} · ` : ''}
            {dataUpdatedAt ? `Last checked ${new Date(dataUpdatedAt).toLocaleTimeString()}` : '—'}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
          >
            {isFetching ? <Loader2 size={15} className="animate-spin" /> : null}
            Refresh
          </button>
        </div>

        <div className="card mt-6">
          <div className="border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">Components</h2>
          </div>
          <Component name="Website" level="operational" note="Cloudflare Pages" />
          <Component name="API" level={apiLevel} note="Cloudflare Workers" />
          <Component name="Database" level={dbLevel} note="Cloudflare D1" />
          <Component name="Video streaming" level="planned" note="Cloudflare Stream · Phase 3" />
          <Component name="Email" level="planned" note="Resend · Phase 6" />
          <Component name="Payments" level="planned" note="Stripe · Phase 7" />
        </div>

        <div className="card mt-6 px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Incident history</h2>
          <p className="mt-1 text-sm text-ink-muted">No incidents reported.</p>
        </div>

        <p className="mt-6 text-center text-xs text-ink-faint">
          BingeBox system status · version {data?.version ?? '—'}
        </p>
      </div>
    </div>
  );
}
